import type { TFunction } from 'i18next';
import { requestCollectionResultsScroll } from './results-scroll';
import '../../../css/modules/timepiece-filter-journey.css';

const BRANDS: Record<string, string> = {
  tudor: 'TUDOR', omega: 'OMEGA', breitling: 'Breitling', 'grand-seiko': 'Grand Seiko',
  longines: 'LONGINES', tissot: 'Tissot', zenith: 'ZENITH', oris: 'ORIS', rado: 'Rado',
  mido: 'MIDO', montblanc: 'Montblanc', gucci: 'Gucci', 'frederique-constant': 'Frederique Constant',
  hamilton: 'Hamilton', movado: 'Movado', bulova: 'BULOVA', accutron: 'ACCUTRON', citizen: 'Citizen', seiko: 'Seiko',
};
const PUBLIC_HOSTS = ['www.raffi-jewellers.ca', 'raffi-jewellers.ca'];
const STAGING_HOST = 'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com';
type Bounds = { start?: number; end?: number };
type Choice = { node: HTMLElement; kind: 'gender' | 'brand' | 'price'; value?: string; bounds?: Bounds };
type Search = { helper: any; on: (event: string, callback: () => void) => unknown };

export function timepieceJourneyRoot(doc: Document, location: Location): HTMLElement | null {
  if (!/^\/(?:fr\/)?collections\/timepieces\/?$/.test(location.pathname)) return null;
  const published = PUBLIC_HOSTS.includes(location.hostname) && doc.querySelector('meta[name="raffi-native-release"][content="20261008"]');
  const preview = location.hostname === STAGING_HOST && doc.querySelector('meta[name="raffi-native-preview"][content="20261008"]');
  if (!(published || preview)) return null;
  const root = doc.querySelector<HTMLElement>('main[data-watch-buying="collection"][data-collection="timepieces-1"]');
  return root && !root.querySelector('.collection-iframe') ? root : null;
}

/** Only the authored price menu determines inclusive bounds; labels never do. */
export function timepiecePriceBounds(path: string, items: unknown): Bounds | undefined {
  const match = path.match(/^\/(?:fr\/)?collections\/timepieces\/(under|over)-(\d+)(k)?\/?$/);
  if (!match || !Array.isArray(items)) return;
  const threshold = Number(match[2]) * (match[3] ? 1000 : 1);
  const valid = items.filter(item => item && (item.start != null || item.end != null)
    && ['start', 'end'].every(key => item[key] == null || typeof item[key] === 'number' && Number.isFinite(item[key]) && item[key] >= 0)
    && (item.start == null || item.end == null || item.start <= item.end));
  const matches = valid.filter(item => match[1] === 'under' ? item.end === threshold : item.start === threshold + 1);
  if (matches.length !== 1) return;
  const item = matches[0];
  return { ...(item.start != null ? { start: item.start } : {}), ...(item.end != null ? { end: item.end } : {}) };
}

function priceSelected(state: any, bounds: Bounds): boolean {
  const refinements = state.numericRefinements?.price || {};
  const expected = { ...(bounds.start != null ? { '>=': [bounds.start] } : {}), ...(bounds.end != null ? { '<=': [bounds.end] } : {}) };
  const keys = Object.keys(refinements).filter(key => refinements[key]?.length);
  return keys.length === Object.keys(expected).length && keys.every(key => key in expected
    && refinements[key].length === 1 && refinements[key][0] === expected[key][0]);
}

/** The same immutable helper state backs these shortcuts and the filter drawer. */
export function timepieceChoiceState(state: any, choice: Pick<Choice, 'kind' | 'value' | 'bounds'>, locale: 'en' | 'fr'): any {
  if (choice.kind === 'price') {
    const selected = priceSelected(state, choice.bounds!);
    let next = state.clearRefinements('price');
    if (!selected) {
      if (choice.bounds!.start != null) next = next.addNumericRefinement('price', '>=', choice.bounds!.start);
      if (choice.bounds!.end != null) next = next.addNumericRefinement('price', '<=', choice.bounds!.end);
    }
    return next.setPage(0);
  }
  const attribute = choice.kind === 'brand' ? 'named_tags.brand' : `named_tags.${locale.toUpperCase()}_gender`;
  const selected: string[] = state.disjunctiveFacetsRefinements?.[attribute] || [];
  if (choice.kind === 'gender') {
    const next = state.clearRefinements(attribute);
    return (selected.length === 1 && selected[0] === choice.value ? next : next.addDisjunctiveFacetRefinement(attribute, choice.value)).setPage(0);
  }
  return (selected.includes(choice.value!) ? state.removeDisjunctiveFacetRefinement(attribute, choice.value)
    : state.addDisjunctiveFacetRefinement(attribute, choice.value)).setPage(0);
}

export function initializeTimepieceFilterJourney(search: Search, t: TFunction): void {
  const root = timepieceJourneyRoot(document, window.location);
  if (!root || root.dataset.timepieceFilterJourney || !search.helper?.state || typeof search.helper.setState !== 'function') return;
  const locale: 'en' | 'fr' = window.location.pathname.startsWith('/fr/') ? 'fr' : 'en';
  const genderAttribute = `named_tags.${locale.toUpperCase()}_gender`;
  const copy = (key: string, values: Record<string, string> = {}) => t(`general:watchDiscovery.${key}`, { ...values, lng: locale });
  const registered = (attribute: string) => search.helper.state.disjunctiveFacets?.includes(attribute);
  let priceItems: unknown = [];
  try { const data = JSON.parse(document.getElementById('priceFilterData')?.textContent || '[]'); priceItems = Array.isArray(data) ? data : data.items; } catch { /* Unverifiable price shortcuts keep their original fallback. */ }
  const choices: Choice[] = [];
  const destination = (node: HTMLElement): URL | null => {
    try {
      const url = new URL(node.getAttribute('href') || node.dataset.fallback || '', window.location.href);
      if (url.protocol !== 'https:' || ![...PUBLIC_HOSTS, STAGING_HOST].includes(url.hostname) || url.port || url.username || url.password || url.hash
        || Array.from(url.searchParams.keys()).some(key => key !== 'lang')) return null;
      return url;
    } catch { return null; }
  };
  root.querySelectorAll<HTMLElement>('.fc-shopping-cards--recipient a.fc-shopping-cards__card').forEach(node => {
    const url = destination(node), match = url?.pathname.match(/^\/(?:fr\/)?collections\/timepieces\/(men|women)\/?$/);
    if (!match || !registered(genderAttribute)) return;
    const her = match[1] === 'women', value = locale === 'fr' ? (her ? 'Pour Elle' : 'Pour Lui') : (her ? 'For Her' : 'For Him');
    choices.push({ node, kind: 'gender', value });
    if (locale === 'fr') {
      const label = node.querySelector('.fc-shopping-cards__label'), cta = node.querySelector('.fc-shopping-cards__cta-line');
      if (label) label.textContent = copy(her ? 'her' : 'him');
      if (cta) cta.textContent = copy(her ? 'shopHer' : 'shopHim');
    }
    node.setAttribute('aria-label', copy(her ? 'her' : 'him'));
  });
  root.querySelectorAll<HTMLElement>('.fc-brand-directory:not(.fc-brand-directory--cards) a.fc-brand-directory__tile').forEach(node => {
    const url = destination(node), slug = url?.pathname.match(/^\/(?:fr\/)?collections\/timepieces\/([a-z0-9-]+)\/?$/)?.[1];
    if (!slug || !Object.prototype.hasOwnProperty.call(BRANDS, slug) || !registered('named_tags.brand')) return;
    choices.push({ node, kind: 'brand', value: BRANDS[slug] });
    node.setAttribute('aria-label', copy('shopAllBrand', { brand: BRANDS[slug] }));
  });
  if (root.querySelector('#filters-byPrice') && !root.querySelector('#priceSlider')) {
    root.querySelectorAll<HTMLElement>('.js-price-pill,.buttons-module--price-nav .price-filter__pill,.js-btn-tier-link').forEach(node => {
      const url = destination(node), bounds = url && timepiecePriceBounds(url.pathname, priceItems);
      if (!bounds) return;
      choices.push({ node, kind: 'price', bounds });
      if (locale === 'fr') {
        const amount = (value: number) => new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 0 }).format(value);
        node.textContent = bounds.start === 0 ? copy('under', { amount: amount(bounds.end!) })
          : /\/over-/.test(url!.pathname) ? copy('over', { amount: amount(bounds.start! - 1) })
            : copy('range', { min: amount(bounds.start!), max: amount(bounds.end!) });
        const title = node.closest('.buttons-module--price-filter')?.querySelector('.price-filter__title');
        if (title) title.textContent = copy('byPrice');
      }
    });
  }
  if (!choices.length) return;
  root.dataset.timepieceFilterJourney = 'v1';
  choices.forEach(choice => {
    choice.node.dataset.timepieceChoice = choice.kind;
    if (choice.node.tagName === 'A') choice.node.setAttribute('role', 'button');
    choice.node.setAttribute('aria-controls', 'collectionWrapper');
  });
  const selected = (choice: Choice): boolean => choice.kind === 'price' ? priceSelected(search.helper.state, choice.bounds!)
    : (search.helper.state.disjunctiveFacetsRefinements?.[choice.kind === 'brand' ? 'named_tags.brand' : genderAttribute] || []).includes(choice.value);
  const sync = () => choices.forEach(choice => {
    const active = selected(choice);
    choice.node.setAttribute('aria-pressed', String(active));
    choice.node.classList.toggle('is-active', active);
  });
  const choiceFor = (target: EventTarget | null) => target instanceof Element
    ? choices.find(choice => choice.node === target || choice.node.contains(target)) : undefined;
  root.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const choice = choiceFor(event.target);
    if (!choice || choice.node.hasAttribute('download') || choice.node.getAttribute('target') && choice.node.getAttribute('target') !== '_self') return;
    event.preventDefault();
    const next = timepieceChoiceState(search.helper.state, choice, locale);
    if (choice.kind === 'price') requestCollectionResultsScroll(true);
    search.helper.setState(next).search();
    sync();
  }, true);
  root.addEventListener('keydown', event => {
    if (event.key !== ' ' || event.repeat || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const choice = choiceFor(event.target);
    if (!choice || choice.node.tagName !== 'A') return;
    event.preventDefault(); choice.node.click();
  });
  search.on('render', sync);
  window.addEventListener('popstate', () => { sync(); window.setTimeout(sync, 450); });
  sync();
}
